import React, { FC } from 'react';
import Form from 'components/workflowOptions/sendForApproval/Form';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PostTimeSaversApi from 'app/api/postTimeSavers';
import PostApi from 'api/post';
import { useTranslation } from 'react-i18next';
import Logger from 'utils/logger';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import { usePage } from 'modules/page/pageSelector';
import AutoScheduleNotice from 'components/workflowOptions/sendForApproval/AutoScheduleNotice';
// [POST-WORKFLOW GATE — TEMPORARY] auto-schedule-on-approval is a new-workflow-only feature;
// remove this gate after full cutover.
import { isPostWorkflowApiNew } from 'app/config/postWorkflowApiMode';

type Props = {
  type: ApprovalType;
  onSelectedUsersChange: (users: number[]) => void;
  initialSelectedUserIds: number[];
  formErrors: Record<string, string>;
  pageIds: number[];
  postIds: number[];
  subject: string | null;
  bodyMessage: string | null;
  additionalEmails?: string;
  onSubjectChange: (value: string) => void;
  onBodyMessageChange: (value: string) => void;
  onAdditionalEmailsChange?: (value: string) => void;
  className?: string;
};

const SendForApprovalForm: FC<Props> = ({
  type,
  initialSelectedUserIds,
  formErrors,
  onSelectedUsersChange,
  pageIds,
  postIds,
  subject,
  bodyMessage,
  additionalEmails,
  onAdditionalEmailsChange,
  onSubjectChange,
  onBodyMessageChange,
  className,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { pages } = usePage();

  const { data, isLoading } = useQuery(
    queryKey.approvalData(pageIds, type),
    () => PostTimeSaversApi.getApprovalData(pageIds, type),
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onError: (error: ApiClientError) => {
        toast(error?.userMessage ?? t('somethingWentWrongTryAgain'), 'error');
        Logger.error(error, 'Cannot load post approval data');
      },
      onSuccess: (approvalData) => {
        if (!subject) {
          onSubjectChange(approvalData.emailSubject);
        }

        if (!bodyMessage) {
          onBodyMessageChange(approvalData.emailBody);
        }
      },
    },
  );

  const uniquePageIds = Array.from(new Set(pageIds));
  const singleProfile =
    type === 'client' && uniquePageIds.length === 1
      ? pages.find((page) => page.id === uniquePageIds[0])
      : undefined;

  const validationQuery = useQuery(
    [queryKey.multipleScheduleValidations, postIds],
    () => PostApi.validate(postIds),
    {
      // [POST-WORKFLOW GATE — TEMPORARY] This validation only feeds the gated AutoScheduleNotice
      // below, so skip the request entirely for non-allowlisted accounts. Remove after cutover.
      enabled:
        isPostWorkflowApiNew() && singleProfile != null && postIds.length > 0,
      refetchOnWindowFocus: false,
      cacheTime: 0,
    },
  );

  const autoScheduleEnabled = singleProfile?.isAutoschedule ?? false;

  const allPostsSchedulable =
    validationQuery.data?.every((validation) => validation.canSchedule) ?? true;

  const isAutoScheduleNoticeReady =
    !autoScheduleEnabled || !validationQuery.isLoading;

  // [POST-WORKFLOW GATE — TEMPORARY] The notice lets the user toggle auto-schedule-on-approval,
  // whose endpoint is gated to allowlisted accounts. Hide it entirely for non-allowlisted
  // accounts so we never offer a control the API would reject. Remove after full cutover.
  const autoScheduleNotice =
    isPostWorkflowApiNew() && singleProfile && isAutoScheduleNoticeReady ? (
      <AutoScheduleNotice
        autoScheduleEnabled={autoScheduleEnabled}
        allPostsSchedulable={allPostsSchedulable}
        profileName={singleProfile.name}
        pageId={singleProfile.id}
      />
    ) : undefined;

  return (
    <Form
      className={className}
      isLoading={isLoading}
      onSelectedUsersChange={onSelectedUsersChange}
      initialSelectedUserIds={initialSelectedUserIds}
      subject={subject ?? ''}
      bodyMessage={bodyMessage ?? ''}
      onSubjectChange={onSubjectChange}
      onBodyMessageChange={onBodyMessageChange}
      formErrors={formErrors}
      type={type}
      availableUsers={data?.users ?? []}
      additionalEmails={additionalEmails}
      onAdditionalEmailsChange={onAdditionalEmailsChange}
      autoScheduleNotice={autoScheduleNotice}
    />
  );
};

export default SendForApprovalForm;
